import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

const tasks = [
    'Eliminating repetition...',
    'Improving clarity...',
    'Strengthening objectives...',
    'Improving conversation flow...',
    'Tightening safety guardrails...',
    'Removing ambiguity...',
    'Reducing hallucination risk...',
    'Improving prompt structure...',
    'Finalizing your instructions...'
];

const CHECK_STEP_DELAY_MS = 900;
const FINAL_PASS_TO_COMPLETE_DELAY_MS = 2500;
const COMPLETE_TO_CONTINUE_DELAY_MS = 2500;

export default function PromptEngineerOptimize({
    optimizationStatus,
    errorMessage,
    runId,
    onStart,
    onTryAgain,
    onContinueWithOriginal,
    onBack,
    onReadyToContinue
}) {
    const helperLines = [
        'Every great writer has an editor.',
        'The AI Instruction Architect will review your instructions one final time to remove repetition, improve clarity, strengthen objectives, and apply prompt engineering best practices.',
        'Estimated time: Less than 30 seconds.'
    ];

    const [visibleCount, setVisibleCount] = useState(1);
    const [completedCount, setCompletedCount] = useState(0);
    const [showCompleteState, setShowCompleteState] = useState(false);
    const [showOptimizationComplete, setShowOptimizationComplete] = useState(false);
    const [isChecklistActive, setIsChecklistActive] = useState(false);
    const progressEndRef = useRef(null);

    useEffect(() => {
        if (optimizationStatus === 'running') {
            setVisibleCount(1);
            setCompletedCount(0);
            setShowCompleteState(false);
            setShowOptimizationComplete(false);
            setIsChecklistActive(true);
            return undefined;
        }

        if (optimizationStatus === 'idle' || optimizationStatus === 'error') {
            setIsChecklistActive(false);
        }

        return undefined;
    }, [optimizationStatus, runId]);

    useEffect(() => {
        if (!isChecklistActive) {
            return undefined;
        }

        if (completedCount >= tasks.length) {
            setIsChecklistActive(false);
            setVisibleCount(tasks.length);
            return undefined;
        }

        const timerId = setTimeout(() => {
            const nextCompletedCount = completedCount + 1;
            setCompletedCount(nextCompletedCount);

            if (nextCompletedCount < tasks.length) {
                setVisibleCount(nextCompletedCount + 1);
                return;
            }

            setVisibleCount(tasks.length);
        }, CHECK_STEP_DELAY_MS);

        return () => {
            clearTimeout(timerId);
        };
    }, [isChecklistActive, completedCount]);

    useEffect(() => {
        if (optimizationStatus !== 'success') {
            return undefined;
        }
        if (completedCount < tasks.length || showCompleteState) {
            return undefined;
        }

        setShowCompleteState(true);

        return undefined;
    }, [optimizationStatus, visibleCount, completedCount, showCompleteState]);

    useEffect(() => {
        if (!showCompleteState) {
            setShowOptimizationComplete(false);
            return undefined;
        }

        const showCompleteTimer = setTimeout(() => {
            setShowOptimizationComplete(true);
        }, FINAL_PASS_TO_COMPLETE_DELAY_MS);

        const continueTimer = setTimeout(() => {
            onReadyToContinue();
        }, FINAL_PASS_TO_COMPLETE_DELAY_MS + COMPLETE_TO_CONTINUE_DELAY_MS);

        return () => {
            clearTimeout(showCompleteTimer);
            clearTimeout(continueTimer);
        };
    }, [showCompleteState, onReadyToContinue]);

    useEffect(() => {
        if (optimizationStatus !== 'running' && !showCompleteState && !showOptimizationComplete) {
            return undefined;
        }

        progressEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return undefined;
    }, [optimizationStatus, visibleCount, showCompleteState, showOptimizationComplete]);

    const isIdle = optimizationStatus === 'idle';
    const isRunning = optimizationStatus === 'running';
    const isSuccess = optimizationStatus === 'success';
    const isError = optimizationStatus === 'error';
    const shouldShowProgress = isRunning || isSuccess;

    const renderedTasks = useMemo(() => tasks.slice(0, visibleCount), [visibleCount]);

    return (
        <Card elevation={2}>
            <CardContent>
                <Stack spacing={2.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AutoFixHighIcon color="primary" />
                        <Typography variant="h6">AI Instruction Architect Optimization</Typography>
                    </Stack>
                    <Typography variant="subtitle1">A second opinion from your AI Instruction Architect.</Typography>
                    <Stack spacing={1.25} alignItems="center">
                        {helperLines.map((line) => (
                            <Chip
                                key={line}
                                label={line}
                                variant="outlined"
                                color="primary"
                                sx={{
                                    bgcolor: 'common.white',
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        fontSize: '0.95rem',
                                        py: 1,
                                        px: 1.5,
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        lineHeight: 1.35
                                    }
                                }}
                            />
                        ))}
                    </Stack>

                    {isIdle && (
                        <Stack direction="row" spacing={2}>
                            <Button variant="contained" onClick={onStart}>
                                ✨ Optimize Instructions
                            </Button>
                            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
                                Back
                            </Button>
                        </Stack>
                    )}

                    {shouldShowProgress && (
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1">Reviewing your instructions...</Typography>
                            {renderedTasks.map((task, index) => {
                                const isComplete = index < completedCount;
                                return (
                                    <Stack key={task} direction="row" spacing={1} alignItems="center">
                                        {isComplete ? (
                                            <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                        ) : (
                                            <RadioButtonUncheckedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                        )}
                                        <Typography variant="body2" color={isComplete ? 'text.primary' : 'text.secondary'}>
                                            {task}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                            {showCompleteState && (
                                <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                    ✨ Applying final prompt engineering pass...
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 1,
                                            opacity: showOptimizationComplete ? 1 : 0,
                                            transition: 'opacity 260ms ease-in-out'
                                        }}
                                    >
                                        ✨ Optimization Complete
                                    </Box>
                                </Typography>
                            )}
                            <Box ref={progressEndRef} sx={{ height: 1 }} />
                        </Stack>
                    )}

                    {isError && (
                        <Stack spacing={2}>
                            <Alert severity="error">We had trouble optimizing your instructions.</Alert>
                            {Boolean(errorMessage) && (
                                <Typography variant="body2" color="text.secondary">{errorMessage}</Typography>
                            )}
                            <Stack direction="row" spacing={2}>
                                <Button variant="contained" onClick={onTryAgain}>
                                    Try Again
                                </Button>
                                <Button variant="text" onClick={onContinueWithOriginal}>
                                    Continue with Original Draft
                                </Button>
                                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
                                    Go Back
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
